import api from 'config/api.config';
import { AxiosResponse } from 'axios';

// Define interfaces for API response data
export interface Price {
  amount_monthly: number;
  amount_annually: number;
  amount_4weeks: number;
  amount_4weeks_annual: number;
  amount_yearly?: number;
  discount_percentage: number;
  currency?: string;
  country?: string;
}

export interface ActionDetail {
  _id: string;
  name: string;
  friendly_name: string;
  category: string;
  platform: string;
  description: string;
  allowed: boolean;
  settings_type: string;
  is_active: boolean;
  status: string;
  created_on: string;
  updated_on: string;
}

export interface Action {
  action_id: string;
  action_limit: number;
  limit_unit?: 'day' | 'week';
  action_details?: ActionDetail;
  limit_utilized?: number;
}

export type LocalizedText = string | { en: string; fr: string };

export interface StripePriceIds {
  monthly?: string;
  annual?: string;
}

export interface SubscriptionPlan {
  _id: string;
  subscription_name: string;
  description: LocalizedText;
  tenure: number;
  price: Price;
  number_of_accounts: number;
  actions: Action[];
  status: string;
  is_active: boolean;
  social_accounts: string[];
  created_at: string;
  supported_platforms: string[];
  tier?: number;
  tagline?: LocalizedText;
  target_audience?: string;
  highlighted_text?: string;
  support_level?: string;
  setup_type?: string;
  volume_type?: string;
  color_indicator?: string;
  features?: {
    likes?: { min: number; max: number; unit: string };
    follow_unfollow?: { enabled: boolean; description: string };
    reposting?: { limit: number; unit: string; description: string };
    ai_comments?: { min: number; max: number; unit: string };
    ai_auto_replies?: { enabled: boolean; features: string[] };
  };
  stripe_price_ids?: StripePriceIds;
}

export interface SubscriptionsResponse {
  plans: SubscriptionPlan[];
  total_plans: number;
}

interface SubscriptionResponse {
  subscription_name: string;
  description: string;
  number_of_accounts: number;
  actions: Action[];
  social_accounts: string[];
}

interface ErrorResponse {
  message?: string;
}

// Fetch all subscriptions
export const getSubscriptions = async (): Promise<SubscriptionsResponse> => {
  try {
    const response: AxiosResponse<SubscriptionsResponse> = await api.get(
      '/api/subscription/get-subscriptions'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch subscriptions'
    );
  }
};

// Fetch a single subscription by userId using GET method
export const getSubscriptionByUserId = async (): Promise<AxiosResponse<SubscriptionResponse | ErrorResponse>> => {
  try {
    const response: AxiosResponse<SubscriptionResponse> = await api.get(
      '/api/subscription/get-subscription'
    );
    return response;
  } catch (error: any) {
    if (error.response) {
      return error.response;
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Upgrade user subscription
export const upgradeSubscription = async (): Promise<AxiosResponse<any>> => {
  try {
    const response: AxiosResponse<any> = await api.post(
      '/api/subscription/upgrade-subscription',
      { subscription_name: 'Starter' }
    );
    return response;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Subscription upgrade failed');
    } else if (error.request) {
      throw new Error('No response from server');
    }
    throw new Error(error.message || 'Failed to upgrade subscription');
  }
};

// Create a new subscription
export const createSubscription = async (subscription_data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.post(
      '/subscriptions',
      subscription_data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to create subscription'
    );
  }
};

// Update an existing subscription
export const updateSubscription = async (id: string, updated_data: any): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.put(
      `/subscriptions/${id}`,
      updated_data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update subscription'
    );
  }
};

// Delete a subscription
export const deleteSubscription = async (id: string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await api.delete(
      `/subscriptions/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to delete subscription'
    );
  }
};
